import { createContext, useLayoutEffect, ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { BaseButton, VisibaCortexBaseButtonProps } from './components_base/base_button.component';
import { v4 } from 'uuid';
import styled, { css } from 'styled-components';
import { Text } from '@visiba-cortex/react-ui-std';
import { tokens } from '@cellula/react-theme-patient';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import { VisuallyHidden } from 'react-aria';
import { Spacer } from '@cellula/react';

type Id = `collapsible-text-${string}`;

interface ReadMoreTextContextProps {
	id: Id;
	isOpen: boolean;
	toggleIsOpen: () => void;
}

const ReadMoreTextContext = createContext<ReadMoreTextContextProps>({} as ReadMoreTextContextProps);

const elements = {
	container: styled.div`
		width: 100%;
		height: 100%;
		position: relative;
	`,
	contentContainer: styled.p<{ maxLineNumber: number }>`
		width: 100%;
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: pre-wrap;
		font: ${tokens.text.body.regular};

		${({ maxLineNumber }) => {
			if (maxLineNumber !== 0)
				return css`
					display: -webkit-box;
					-webkit-line-clamp: ${maxLineNumber};
					line-clamp: ${maxLineNumber};
					-webkit-box-orient: vertical;
					text-overflow: ellipsis;
				`;

			return null;
		}};
	`,
};

interface Props {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	children?: ReactNode;
	maxLineNumber?: number;
	title?: string;
}

export function ReadMoreText({ children, open, onOpenChange, title, maxLineNumber = 3, ...rest }: Props): JSX.Element {
	const id = useMemo(() => {
		return `collapsible-text-${v4().substring(0, 8)}` as Id;
	}, []);
	const ref = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(open ?? false);
	const [showButton, setShowButton] = useState(false);

	function toggleIsOpen(): void {
		const open = !isOpen;

		setIsOpen(open);

		if (onOpenChange) {
			onOpenChange(open);
		}
	}

	useLayoutEffect(
		function whenComponentMounts() {
			if (ref.current == null) return;
			// Only shows the "show more" button if the child's height exceeds where the parent y-overflows".
			setShowButton(maxLineNumber === 0 ? true : ref.current.scrollHeight > ref.current.clientHeight);
		},
		[maxLineNumber],
	);

	return (
		<ReadMoreTextContext.Provider value={{ id, isOpen, toggleIsOpen }} {...rest}>
			<elements.container {...rest}>
				<VisuallyHidden>
					<h4>{title}</h4>
				</VisuallyHidden>
				<elements.contentContainer ref={ref} id={id} maxLineNumber={isOpen ? 0 : maxLineNumber}>
					{children}
				</elements.contentContainer>
				<Spacer vertical={tokens.spacing.x1} />
				{showButton ? <CollapsibleTrigger /> : null}
			</elements.container>
		</ReadMoreTextContext.Provider>
	);
}

interface TriggerProps extends VisibaCortexBaseButtonProps {}

function CollapsibleTrigger(_: TriggerProps): JSX.Element {
	const { isOpen, toggleIsOpen } = useContext(ReadMoreTextContext);

	function handleClick(): void {
		toggleIsOpen();
	}

	return (
		<BaseButton tabIndex={-1} onClick={handleClick}>
			<Text color={tokens.color.content.interactive} font={tokens.text.label.semiBold}>
				{withTranslation(isOpen ? 'PW.Component.Collapsible.ShowLess' : 'PW.Component.Collapsible.ShowMore')}
			</Text>
		</BaseButton>
	);
}
